import { useEffect, useMemo, useState } from 'react';
import { Button, Form, Modal, Table } from 'react-bootstrap';
import CurrencyInput from 'react-currency-input-field';
import { useDispatch } from 'react-redux';

import { obtenerPaquete } from '../../../../actions/configuracion';

const formatNumber = (value) => {
    const v = Number(value || 0);
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(v);
};

/* Agregar un paquete a una orden de salida: igual que en la cotización, aquí tampoco se toca
   inventario ni se elige ubicación — eso se define hasta la entrega (EntregarOrdenModal). El
   backend solo necesita {paquete, cantidad, precio, iva} y congela el snapshot de componentes
   desde el catálogo. Los componentes mostrados aquí son solo de referencia. */
export default function SeleccionPaqueteOrdenModal({ show, onHide, onConfirm, paquete }) {
    const dispatch = useDispatch();

    const [cargando, setCargando] = useState(false);
    const [receta, setReceta] = useState(null);

    const [cantidadPaquetes, setCantidadPaquetes] = useState(1);
    const [precio, setPrecio] = useState(0);
    const [ivaPaquete, setIvaPaquete] = useState(false);

    useEffect(() => {
        if (!show || !paquete?._id) {
            setReceta(null);
            return;
        }

        const cargar = async () => {
            setCargando(true);

            const detalle = await dispatch(obtenerPaquete(paquete._id));
            const componentes = Array.isArray(detalle?.productos) ? detalle.productos : [];
            setReceta({
                _id: detalle?._id,
                nombre: detalle?.nombre,
                codigo: detalle?.codigo,
                componentes
            });

            setCargando(false);
        };

        cargar();
    }, [show, paquete, dispatch]);

    useEffect(() => {
        if (!show) return;

        setCantidadPaquetes(1);
        setPrecio(Number(paquete?.precio || 0));
        setIvaPaquete(!!paquete?.iva);
    }, [show, paquete]);

    const componentes = useMemo(() => (Array.isArray(receta?.componentes) ? receta.componentes : []), [receta]);

    const puedeConfirmar = !cargando && Number(cantidadPaquetes) > 0 && componentes.length > 0;

    const handleConfirm = () => {
        if (!puedeConfirmar) return;

        onConfirm({
            paqueteId: receta._id,
            paqueteNombre: receta.nombre,
            paqueteCodigo: receta.codigo,
            cantidad: Number(cantidadPaquetes || 0),
            precio: Number(precio || 0),
            iva: ivaPaquete,
            componentes: componentes.map((c) => ({
                productoId: c?.producto?._id,
                productoNombre: c?.producto?.nombre || '',
                unidadNombre: c?.unidad?.nombre || '',
                cantidadPorPaquete: Number(c?.cantidad || 0),
                conversion: Number(c?.conversion || 1) || 1
            }))
        });
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" backdrop="static" keyboard={false}>
            <Modal.Header closeButton>
                <Modal.Title>Agregar paquete</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div className="mb-3">
                    <div className="fw-semibold">{paquete?.nombre || ''}</div>
                    {paquete?.codigo ? (
                        <div className="text-muted" style={{ fontSize: 12 }}>{paquete.codigo}</div>
                    ) : null}
                </div>

                <div className="row g-3 mb-3">
                    <div className="col-md-4">
                        <div className="text-muted mb-1" style={{ fontSize: 12 }}>Cantidad de paquetes</div>
                        <Form.Control
                            type="number"
                            value={cantidadPaquetes}
                            min={1}
                            onChange={(e) => setCantidadPaquetes(e.target.value === '' ? '' : Number(e.target.value))}
                        />
                    </div>
                    <div className="col-md-4">
                        <div className="text-muted mb-1" style={{ fontSize: 12 }}>Precio final</div>
                        <CurrencyInput
                            value={precio}
                            decimalsLimit={3}
                            allowNegativeValue={false}
                            className="form-control"
                            prefix="$ "
                            groupSeparator=","
                            decimalSeparator="."
                            onValueChange={(value) => setPrecio(value === undefined || value === null || value === '' ? 0 : Number(value))}
                        />
                    </div>
                    <div className="col-md-4 d-flex align-items-end">
                        <Form.Check
                            type="checkbox"
                            label="IVA"
                            checked={ivaPaquete}
                            onChange={() => setIvaPaquete(!ivaPaquete)}
                        />
                    </div>
                </div>

                {cargando ? (
                    <div className="text-muted text-center py-3">Cargando receta del paquete...</div>
                ) : (
                    <div className="table-responsive">
                        <Table bordered hover className="mb-0">
                            <thead>
                                <tr>
                                    <th>Producto</th>
                                    <th style={{ width: 110 }} className="text-center">Cantidad</th>
                                </tr>
                            </thead>
                            <tbody>
                                {componentes.length === 0 && (
                                    <tr>
                                        <td colSpan={2} className="text-center text-muted">
                                            Este paquete no tiene productos configurados
                                        </td>
                                    </tr>
                                )}

                                {componentes.map((c) => {
                                    const idProducto = c?.producto?._id;
                                    const conversion = Number(c?.conversion || 1) || 1;
                                    const necesaria = (Number(c?.cantidad || 0) * Number(cantidadPaquetes || 0)) / conversion;

                                    return (
                                        <tr key={idProducto}>
                                            <td className="align-middle">
                                                <div className="fw-semibold">{c?.producto?.nombre || 'Sin definir'}</div>
                                                {c?.producto?.codigo ? (
                                                    <div className="text-muted" style={{ fontSize: 12 }}>{c.producto.codigo}</div>
                                                ) : null}
                                            </td>
                                            <td className="text-center align-middle">
                                                {formatNumber(necesaria)} {c?.unidad?.nombre || ''}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>
                )}

                <div className="text-muted mt-3" style={{ fontSize: 13 }}>
                    La ubicación de cada producto del paquete se definirá hasta la entrega de la orden.
                </div>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Cancelar
                </Button>
                <Button variant="success" onClick={handleConfirm} disabled={!puedeConfirmar}>
                    Agregar
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
